/**
 * SignupService for handling user registration operations.
 * Uses the AWS Cognito Identity Provider SDK directly — no Amplify dependency.
 */

import {
  CognitoIdentityProviderClient,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

const cognitoClient = new CognitoIdentityProviderClient({
  region: process.env.NEXT_PUBLIC_AWS_REGION ?? 'ap-south-1',
});

const USER_POOL_CLIENT_ID = process.env.NEXT_PUBLIC_USER_POOL_CLIENT_ID ?? '';

export interface SignupFormData {
  email: string;
  password: string;
  mobile: string;
  /** Defaults to "untitled" when omitted or blank */
  company?: string;
  /** Optional UTM / ad tracking parameters captured from the signup page URL */
  utmParams?: Record<string, string>;
}

const DEFAULT_COMPANY_NAME = "untitled";

export interface SignupResponse {
  success: boolean;
  message: string;
  data?: any;
  requiresVerification?: boolean;
  /** True when SignUp hit an existing UNCONFIRMED user and OTP was resent. */
  resumedPendingVerification?: boolean;
}

export interface VerifyOTPResponse {
  success: boolean;
  message: string;
  data?: any;
}

/** Handles user signup with email, password, and mobile. */
export const signup = async (formData: SignupFormData): Promise<SignupResponse> => {
  try {
    const { email, password, mobile, company, utmParams } = formData;
    const companyName = company?.trim() || DEFAULT_COMPANY_NAME;

    let formattedMobile = mobile.trim();
    try {
      const parsed = parsePhoneNumber(formattedMobile, 'IN');
      if (parsed) formattedMobile = parsed.format('E.164');
    } catch {
      if (!formattedMobile.startsWith('+')) {
        formattedMobile = formattedMobile.startsWith('91')
          ? `+${formattedMobile}`
          : `+91${formattedMobile}`;
      }
    }

    const userAttributes = [
      { Name: 'email',          Value: email },
      { Name: 'custom:company', Value: companyName },
      { Name: 'phone_number',   Value: formattedMobile },
    ];

    // Persist UTM as custom:utm (String on the user pool). Do not pass raw UTM keys via
    // ClientMetadata — underscore-prefixed keys (_gl, _gcl_aw, …) fail Cognito validation.
    if (utmParams && Object.keys(utmParams).length > 0) {
      try {
        const utmQueryString = new URLSearchParams(utmParams).toString();
        if (utmQueryString) {
          userAttributes.push({ Name: 'custom:utm', Value: utmQueryString.slice(0, 2048) });
        }
      } catch (_) {
        const utmQueryString = Object.entries(utmParams)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join('&');
        if (utmQueryString) {
          userAttributes.push({ Name: 'custom:utm', Value: utmQueryString.slice(0, 2048) });
        }
      }
    }

    await cognitoClient.send(
      new SignUpCommand({
        ClientId: USER_POOL_CLIENT_ID,
        Username: email,
        Password: password,
        UserAttributes: userAttributes,
      })
    );

    return {
      success: true,
      message: 'Account created successfully! Please check your mobile for the verification code.',
      requiresVerification: true,
    };
  } catch (error: any) {
    console.error('Signup error:', error);

    // Cognito keeps UNCONFIRMED users after SignUp until OTP succeeds.
    // A later SignUp with the same email throws UsernameExistsException even
    // though the account was never finished — resend OTP so they can continue.
    if (error.name === 'UsernameExistsException') {
      try {
        await cognitoClient.send(
          new ResendConfirmationCodeCommand({
            ClientId: USER_POOL_CLIENT_ID,
            Username: formData.email,
          })
        );
        return {
          success: true,
          message:
            'We found an unfinished signup for this email. A new verification code has been sent.',
          requiresVerification: true,
          resumedPendingVerification: true,
        };
      } catch (resendError: any) {
        // Resend only works for UNCONFIRMED users. Confirmed accounts (or other
        // failures) surface as "already exists" so the user can sign in instead.
        console.error('Resend after UsernameExistsException failed:', resendError);
        return {
          success: false,
          message: 'An account with this email already exists. Please sign in instead.',
        };
      }
    }

    let errorMessage = 'Signup failed. Please try again.';

    switch (error.name) {
      case 'InvalidPasswordException':
        errorMessage = 'Password does not meet requirements.';
        break;
      case 'InvalidParameterException':
        errorMessage = 'Invalid email or password format.';
        break;
      case 'NotAuthorizedException':
        errorMessage = 'User registration is not allowed.';
        break;
      case 'TooManyRequestsException':
        errorMessage = 'Too many attempts. Please try again later.';
        break;
      default:
        // Unknown Cognito/AWS errors are already logged above — never surface raw infrastructure messages.
        break;
    }

    return { success: false, message: errorMessage };
  }
};

export const validateGSTIN = (gstin: string): boolean => {
  if (!gstin || gstin.length !== 15) return false;
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return gstinRegex.test(gstin.toUpperCase());
};

export const validatePassword = (password: string): { isValid: boolean; message?: string } => {
  if (!password) return { isValid: false, message: 'Password is required' };
  if (password.length < 8) return { isValid: false, message: 'Password must be at least 8 characters long' };
  if (!/(?=.*[a-z])/.test(password)) return { isValid: false, message: 'Password must contain at least one lowercase letter' };
  if (!/(?=.*[A-Z])/.test(password)) return { isValid: false, message: 'Password must contain at least one uppercase letter' };
  if (!/(?=.*\d)/.test(password)) return { isValid: false, message: 'Password must contain at least one number' };
  if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(password)) return { isValid: false, message: 'Password must contain at least one special character' };
  return { isValid: true };
};

export const validateEmail = (email: string): { isValid: boolean; message?: string } => {
  if (!email || !email.trim()) return { isValid: false, message: 'Email is required' };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return { isValid: false, message: 'Please enter a valid email address' };
  if (email.length > 254) return { isValid: false, message: 'Email address is too long' };
  return { isValid: true };
};

export const validateMobile = (mobile: string): { isValid: boolean; message?: string } => {
  if (!mobile || !mobile.trim()) return { isValid: false, message: 'Mobile number is required' };
  const trimmed = mobile.trim();
  if (isValidPhoneNumber(trimmed) || isValidPhoneNumber(trimmed, 'IN')) {
    return { isValid: true };
  }
  return { isValid: false, message: 'Please enter a valid mobile number with country code' };
};

export const verifyOTP = async (email: string, otp: string): Promise<VerifyOTPResponse> => {
  try {
    await cognitoClient.send(
      new ConfirmSignUpCommand({
        ClientId: USER_POOL_CLIENT_ID,
        Username: email,
        ConfirmationCode: otp,
      })
    );
    return { success: true, message: 'Mobile number verified successfully! You can now sign in.' };
  } catch (error: any) {
    console.error('OTP verification error:', error);
    let errorMessage = 'OTP verification failed. Please try again.';
    switch (error.name) {
      case 'CodeMismatchException':           errorMessage = 'Invalid verification code. Please check and try again.'; break;
      case 'ExpiredCodeException':            errorMessage = 'Verification code has expired. Please request a new one.'; break;
      case 'NotAuthorizedException':          errorMessage = 'User is not authorized for this action.'; break;
      case 'UserNotFoundException':           errorMessage = 'User not found. Please sign up first.'; break;
      case 'TooManyFailedAttemptsException':  errorMessage = 'Too many failed attempts. Please try again later.'; break;
      case 'LimitExceededException':          errorMessage = 'Attempt limit exceeded. Please try again later.'; break;
      default:
        // Unknown Cognito/AWS errors (e.g. PostConfirmation Lambda timeouts) are already logged above.
        break;
    }
    return { success: false, message: errorMessage };
  }
};

export const validateOTP = (otp: string): { isValid: boolean; message?: string } => {
  if (!otp || !otp.trim()) return { isValid: false, message: 'Verification code is required' };
  const cleanOTP = otp.replace(/\D/g, '');
  if (cleanOTP.length !== 6) return { isValid: false, message: 'Please enter a valid 6-digit verification code' };
  return { isValid: true };
};

export const resendOTP = async (email: string): Promise<SignupResponse> => {
  try {
    await cognitoClient.send(
      new ResendConfirmationCodeCommand({
        ClientId: USER_POOL_CLIENT_ID,
        Username: email,
      })
    );
    return { success: true, message: 'A new verification code has been sent to your mobile number.', requiresVerification: true };
  } catch (error: any) {
    console.error('Resend OTP error:', error);
    let errorMessage = 'Failed to send verification code. Please try again.';
    switch (error.name) {
      case 'LimitExceededException':    errorMessage = 'Too many attempts. Please try again later.'; break;
      case 'UserNotFoundException':     errorMessage = 'User not found. Please sign up first.'; break;
      case 'InvalidParameterException': errorMessage = 'Invalid email format.'; break;
      default:
        // Unknown Cognito/AWS errors are already logged above — never surface raw infrastructure messages.
        break;
    }
    return { success: false, message: errorMessage };
  }
};
